/**
 * Detects login source and physical device platform from the browser environment.
 *
 * loginSource (storage keeps capitalized values for existing callers):
 * - "Mobile" — any Android / iPhone / iPad session (browser OR WebView)
 * - "Web"    — Windows / macOS / Linux / ChromeOS desktop or laptop only
 *
 * deviceType:
 * - "Android" | "iPhone" | "iPad" | "Web"
 *
 * ---------------------------------------------------------------------------
 * ROOT CAUSE — why Chrome Android was "Web" while Edge / Firefox were correct:
 *
 * Older code treated ONLY native WebViews as Mobile, so every mobile browser
 * became "Web". After switching to OS-based detection, Edge/Firefox still
 * worked because their userAgent keeps an "Android" / "iPhone" token.
 *
 * Chrome is different:
 *  1) "Request desktop site" (and desktop-by-default on large Android tablets)
 *     rewrites BOTH navigator.userAgent AND navigator.userAgentData to a
 *     Linux desktop identity — platform becomes "Linux", mobile becomes false,
 *     and the UA loses the "Android" token. Client Hints alone are therefore
 *     NOT enough when desktop mode is active.
 *  2) The underlying navigator.platform often remains ARM Linux
 *     ("Linux armv8l" / "Linux aarch64") and maxTouchPoints stays > 0.
 *     Combining those signals recovers Android without using viewport width.
 *
 * Priority: Android → iPhone → iPad → Chrome UA-spoof recovery →
 *           Client Hints / WebView → Desktop.
 * Never classify by viewport / window width alone.
 * ---------------------------------------------------------------------------
 */

/** Set false before production commit to silence detection traces. */
const DEBUG_DEVICE_DETECTION = true;

const STORAGE_KEYS = {
    loginSource: 'loginSource',
    isMobileApp: 'isMobileApp',
    spliytNativeApp: 'spliytNativeApp',
    deviceType: 'deviceType',
};

const MOBILE_FLAG_PARAMS = ['isMobileApp', 'mobileApp', 'nativeApp'];

/** Custom native-app user-agent tokens injected by the Android / iOS shells. */
const MOBILE_USER_AGENT_PATTERNS = [
    /SpliytMobileApp/i,
    /BrikTokMobile/i,
    /SpliytApp\/\d/i,
    /BrikTokApp\/\d/i,
];

const IOS_MESSAGE_HANDLER_NAMES = [
    'spliytNative',
    'SpliytNative',
    'SpliytNativeApp',
    'nativeApp',
    'NativeApp',
    'bridge',
    'appBridge',
    'cordova',
    'flutter_inappwebview',
];

function safeGetStorage(storage, key) {
    try {
        if (!storage || typeof storage.getItem !== 'function' || key == null) {
            return null;
        }
        return storage.getItem(key);
    } catch {
        return null;
    }
}

function safeSetStorage(storage, key, value) {
    try {
        if (!storage || typeof storage.setItem !== 'function' || key == null || value == null) {
            return;
        }
        storage.setItem(key, value);
    } catch {
        // Ignore storage failures (private mode, WebView restrictions).
    }
}

function persistMobileLoginSource() {
    safeSetStorage(sessionStorage, STORAGE_KEYS.loginSource, 'Mobile');
    safeSetStorage(localStorage, STORAGE_KEYS.loginSource, 'Mobile');
}

function persistMobileSession() {
    persistMobileLoginSource();
    safeSetStorage(sessionStorage, STORAGE_KEYS.isMobileApp, 'true');
    safeSetStorage(sessionStorage, STORAGE_KEYS.spliytNativeApp, 'true');
    safeSetStorage(localStorage, STORAGE_KEYS.isMobileApp, 'true');
    safeSetStorage(localStorage, STORAGE_KEYS.spliytNativeApp, 'true');
}

/**
 * Persist Web only when live detection agrees we are on a desktop OS.
 * Prevents any caller from forcing "Web" over a real phone/tablet session
 * (stale bookmarks, ?loginSource=web, or race after localStorage.clear).
 */
function persistWebSession() {
    const liveType = detectDeviceType();
    if (liveType === 'Android' || liveType === 'iPhone' || liveType === 'iPad') {
        persistMobileLoginSource();
        safeSetStorage(localStorage, STORAGE_KEYS.deviceType, liveType);
        return;
    }

    safeSetStorage(sessionStorage, STORAGE_KEYS.loginSource, 'Web');
    safeSetStorage(localStorage, STORAGE_KEYS.loginSource, 'Web');
    safeSetStorage(sessionStorage, STORAGE_KEYS.isMobileApp, 'false');
    safeSetStorage(localStorage, STORAGE_KEYS.isMobileApp, 'false');
    safeSetStorage(localStorage, STORAGE_KEYS.deviceType, 'Web');
}

function isTruthyFlag(value) {
    if (value == null) {
        return false;
    }
    const normalized = String(value).trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
}

function getWindowSearch(search) {
    if (search != null && search !== '') {
        return String(search);
    }
    if (typeof window === 'undefined' || !window.location) {
        return '';
    }
    return window.location.search || '';
}

/** Collect every navigator signal we use (also used for debug logging). */
function getNavigatorSignals() {
    const empty = {
        ua: '',
        appVersion: '',
        platform: '',
        vendor: '',
        maxTouchPoints: 0,
        uaDataPlatform: '',
        uaDataMobile: null,
        hasUaData: false,
    };

    if (typeof navigator === 'undefined') {
        return empty;
    }

    let uaDataPlatform = '';
    let uaDataMobile = null;
    let hasUaData = false;

    try {
        if (navigator.userAgentData) {
            hasUaData = true;
            uaDataPlatform = String(navigator.userAgentData.platform || '');
            uaDataMobile =
                typeof navigator.userAgentData.mobile === 'boolean'
                    ? navigator.userAgentData.mobile
                    : null;
        }
    } catch {
        // Client Hints can throw under some privacy / iframe policies (Chrome).
    }

    return {
        ua: String(navigator.userAgent || ''),
        appVersion: String(navigator.appVersion || ''),
        platform: String(navigator.platform || ''),
        vendor: String(navigator.vendor || ''),
        maxTouchPoints: Number(navigator.maxTouchPoints || 0),
        uaDataPlatform,
        uaDataMobile,
        hasUaData,
    };
}

function logDetectionTrace(label, extra) {
    if (!DEBUG_DEVICE_DETECTION) {
        return;
    }

    try {
        const s = getNavigatorSignals();
        console.group('Device Detection');
        console.log('Label:', label);
        console.log('UserAgent:', s.ua);
        console.log(
            'UserAgentData:',
            s.hasUaData ? { platform: s.uaDataPlatform, mobile: s.uaDataMobile } : null
        );
        console.log('Platform:', s.platform);
        console.log('Vendor:', s.vendor);
        console.log('MaxTouchPoints:', s.maxTouchPoints);
        if (extra && extra.loginSource != null) {
            console.log('Detected loginSource:', extra.loginSource);
        }
        if (extra && extra.deviceType != null) {
            console.log('Detected deviceType:', extra.deviceType);
        }
        if (extra) {
            console.log('Extra:', extra);
        }
        console.groupEnd();
    } catch {
        // Logging must never break detection.
    }
}

export function persistLoginSourceFromUrl(search) {
    const query = getWindowSearch(search);
    const params = new URLSearchParams(query);

    const explicitSource = params.get('loginSource');
    if (explicitSource) {
        const normalized = String(explicitSource).trim().toLowerCase();
        if (normalized === 'mobile') {
            persistMobileSession();
            return 'Mobile';
        }
        if (normalized === 'web') {
            // Do NOT honor ?loginSource=web on a real phone/tablet — Chrome
            // deep-links or stale bookmarks must not force Web on mobile OS.
            const liveType = detectDeviceType();
            if (liveType === 'Android' || liveType === 'iPhone' || liveType === 'iPad') {
                persistMobileLoginSource();
                return 'Mobile';
            }
            persistWebSession();
            return 'Web';
        }
    }

    for (const flag of MOBILE_FLAG_PARAMS) {
        if (isTruthyFlag(params.get(flag))) {
            persistMobileSession();
            return 'Mobile';
        }
    }

    return null;
}

function hasNativeBridge() {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return !!(
            window.SpliytNative ||
            window.SpliytNativeApp ||
            window.SpliytAndroid ||
            window.ReactNativeWebView ||
            window.Capacitor ||
            window.cordova ||
            window.webkit?.messageHandlers?.spliytNative ||
            window.webkit?.messageHandlers?.SpliytNative ||
            window.webkit?.messageHandlers?.SpliytNativeApp ||
            window.webkit?.messageHandlers?.nativeApp ||
            (typeof window.Android?.isNativeApp === 'function' && window.Android.isNativeApp())
        );
    } catch {
        return false;
    }
}

function isDesktopOsToken(signals) {
    const s = signals || getNavigatorSignals();
    if (/Windows NT|Win64|Windows Phone|Macintosh|Mac OS X|CrOS|Chrome OS/i.test(s.ua)) {
        return true;
    }
    if (/Windows|macOS|Mac OS|Chrome OS/i.test(s.uaDataPlatform)) {
        return true;
    }
    return false;
}

/**
 * Android phone/tablet — Chrome, Edge, Firefox, Samsung Internet, WebView.
 * UA token is authoritative; Client Hints recover non-spoofed Chrome sessions.
 */
function isAndroidPlatform(signals) {
    const s = signals || getNavigatorSignals();

    // 1) Classic UA / appVersion (Firefox, Edge, Samsung, normal Chrome).
    if (/Android/i.test(s.ua) || /Android/i.test(s.appVersion)) {
        return true;
    }

    // Some OEM / WebView UAs abbreviate Android as "Adr".
    if (/\bAdr[\s/;]/i.test(s.ua)) {
        return true;
    }

    // 2) Chrome Client Hints when desktop-site is OFF (platform still "Android").
    if (/Android/i.test(s.uaDataPlatform)) {
        return true;
    }

    // 3) Chrome desktop-site / tablet desktop-by-default spoof recovery.
    if (isAndroidDesktopUaSpoof(s)) {
        return true;
    }

    return false;
}

/**
 * Recover Android when Chrome rewrote UA + Client Hints to a Linux desktop
 * identity. Uses platform architecture + touch + Chromium signals together
 * (never viewport width, never touch alone).
 */
function isAndroidDesktopUaSpoof(signals) {
    const s = signals || getNavigatorSignals();

    // Explicit mobile tokens mean this path is unnecessary.
    if (/Android|iPhone|iPod|iPad/i.test(s.ua) || /Android/i.test(s.uaDataPlatform)) {
        return false;
    }

    // Real desktop / ChromeOS — never treat as Android.
    if (isDesktopOsToken(s)) {
        return false;
    }

    const platformBlob = `${s.platform} ${s.appVersion}`;
    const isArmLinux =
        /arm|aarch64|aarch/i.test(s.platform) ||
        /Linux (arm|aarch)/i.test(platformBlob);

    // Chromium family on Android (Chrome / Edge Android / Samsung / WebView).
    // Firefox does not expose userAgentData, so this path is Chrome-specific —
    // which matches the reported bug (only Chrome fails).
    const isChromium =
        s.hasUaData ||
        (/Chrome\//i.test(s.ua) && !/Firefox/i.test(s.ua));

    const looksLikeLinuxDesktopUa =
        /Linux/i.test(s.ua) &&
        /Chrome\//i.test(s.ua) &&
        !/Mobile|Android|iPhone|iPad/i.test(s.ua);

    const hasTouch = s.maxTouchPoints > 0;

    // Strongest sync recovery: ARM Linux Chromium + touch.
    // Real Linux desktops are almost always x86_64; Android keeps ARM in
    // navigator.platform even when the UA string is fully desktop-spoofed.
    if (isArmLinux && isChromium && hasTouch) {
        return true;
    }

    // Chrome large-tablet desktop-by-default can also spoof platform to
    // "Linux x86_64". Phones/tablets still expose several touch points
    // (typically 5+). Combine Chromium + Linux desktop UA + multi-touch.
    if (
        looksLikeLinuxDesktopUa &&
        isChromium &&
        s.maxTouchPoints >= 5 &&
        (/Linux/i.test(s.uaDataPlatform) || /Linux/i.test(s.platform) || !s.uaDataPlatform)
    ) {
        return true;
    }

    // userAgentData.mobile === true must never be classified as Web, even if
    // the platform string is missing / reduced.
    if (s.uaDataMobile === true && isChromium && !isDesktopOsToken(s)) {
        return true;
    }

    return false;
}

/** iPhone / iPod only (not iPad). */
function isIPhonePlatform(signals) {
    const s = signals || getNavigatorSignals();
    if (/iPhone|iPod/i.test(s.ua)) {
        return true;
    }
    // Rare Client Hints path (mostly unused on iOS Safari, present on some Chromium builds).
    if (/^iOS$/i.test(s.uaDataPlatform) && !/iPad/i.test(s.ua) && s.maxTouchPoints <= 1) {
        return true;
    }
    return false;
}

/** iPad / iPadOS (incl. iPadOS 13+ MacIntel + multi-touch). */
function isIPadPlatform(signals) {
    const s = signals || getNavigatorSignals();

    if (/iPad/i.test(s.ua)) {
        return true;
    }

    // iPadOS 13+: reports as Macintosh / MacIntel with multi-touch.
    if (
        (s.platform === 'MacIntel' || /Macintosh/i.test(s.ua)) &&
        s.maxTouchPoints > 1 &&
        !/iPhone|iPod/i.test(s.ua)
    ) {
        return true;
    }

    return false;
}

function isIOSPlatform(signals) {
    return isIPhonePlatform(signals) || isIPadPlatform(signals);
}

function isIOSWebView(signals) {
    if (typeof window === 'undefined' || !isIOSPlatform(signals)) {
        return false;
    }

    try {
        const handlers = window.webkit && window.webkit.messageHandlers;
        if (handlers && typeof handlers === 'object') {
            for (let i = 0; i < IOS_MESSAGE_HANDLER_NAMES.length; i++) {
                const name = IOS_MESSAGE_HANDLER_NAMES[i];
                try {
                    if (name in handlers || handlers[name] != null) {
                        return true;
                    }
                } catch {
                    // Ignore per-handler access errors
                }
            }

            try {
                const ownNames = Object.getOwnPropertyNames(handlers);
                if (ownNames && ownNames.length > 0) {
                    return true;
                }
            } catch {
                // Ignore
            }
        }

        const ua = (signals || getNavigatorSignals()).ua;
        if (/AppleWebKit/i.test(ua) && !/Safari/i.test(ua)) {
            return true;
        }

        if (window.ReactNativeWebView != null || window.Capacitor != null || window.cordova != null) {
            return true;
        }
    } catch {
        return false;
    }

    return false;
}

function isAndroidWebView(signals) {
    if (typeof window === 'undefined' || !isAndroidPlatform(signals)) {
        return false;
    }

    try {
        const ua = (signals || getNavigatorSignals()).ua;

        if (/;\s*wv\)/i.test(ua)) {
            return true;
        }

        if (MOBILE_USER_AGENT_PATTERNS.some((pattern) => pattern.test(ua))) {
            return true;
        }

        if (
            window.ReactNativeWebView != null ||
            window.Capacitor != null ||
            window.cordova != null ||
            (typeof window.Android === 'object' && window.Android != null)
        ) {
            return true;
        }
    } catch {
        return false;
    }

    return false;
}

function hasMobileUserAgent(signals) {
    const s = signals || getNavigatorSignals();
    if (!s.ua) {
        return false;
    }

    if (MOBILE_USER_AGENT_PATTERNS.some((pattern) => pattern.test(s.ua))) {
        return true;
    }

    if (/;\s*wv\)/i.test(s.ua)) {
        return true;
    }

    return isIOSWebView(s) || isAndroidWebView(s);
}

/**
 * Chrome Client Hints "mobile" bit — true on real phones even when the UA
 * string was spoofed (when desktop-site is OFF). When desktop-site is ON,
 * mobile is false and platform may be "Linux" — handled by isAndroidDesktopUaSpoof.
 */
function resolveFromClientHints(signals) {
    const s = signals || getNavigatorSignals();

    if (/Android/i.test(s.uaDataPlatform)) {
        return 'Android';
    }

    if (/^iOS$/i.test(s.uaDataPlatform)) {
        return isIPadPlatform(s) ? 'iPad' : 'iPhone';
    }

    // mobile:true with unknown platform — still never classify as Web.
    if (s.uaDataMobile === true) {
        if (isIPadPlatform(s)) {
            return 'iPad';
        }
        if (/iPhone|iPod/i.test(s.ua)) {
            return 'iPhone';
        }
        if (/android|linux/i.test(s.platform) || /Linux/i.test(s.ua) || isAndroidDesktopUaSpoof(s)) {
            return 'Android';
        }
        // Safe default for Chromium mobile Client Hints without a clear OS label.
        return 'Android';
    }

    return null;
}

/**
 * Detect the physical device platform.
 * Priority: Android → iPhone → iPad → Client Hints / WebView → Desktop (Web)
 */
export function detectDeviceType() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return 'Web';
    }

    const s = getNavigatorSignals();

    // 1) Android (any browser or WebView) — UA, Client Hints, Chrome spoof recovery.
    if (isAndroidPlatform(s)) {
        return 'Android';
    }

    // 2) iPhone / iPod
    if (isIPhonePlatform(s)) {
        return 'iPhone';
    }

    // 3) iPad / iPadOS
    if (isIPadPlatform(s)) {
        return 'iPad';
    }

    // 4a) Chrome Client Hints recovery (non-spoofed desktop-site off).
    const fromHints = resolveFromClientHints(s);
    if (fromHints) {
        return fromHints;
    }

    // 4b) Embedded WebView / native shell without a clear OS token.
    if (hasNativeBridge() || hasMobileUserAgent(s)) {
        if (isAndroidWebView(s) || (typeof window !== 'undefined' && window.Android)) {
            return 'Android';
        }
        if (isIOSWebView(s) || isIOSPlatform(s)) {
            return isIPadPlatform(s) ? 'iPad' : 'iPhone';
        }
        // Native bridge present but OS unknown — prefer Android for custom shells
        // that inject window.Android / Capacitor on Android builds.
        if (typeof window !== 'undefined' && (window.Android || window.Capacitor || window.cordova)) {
            return 'Android';
        }
    }

    // 5) Desktop / laptop only
    return 'Web';
}

export function isMobileDevice() {
    const deviceType = detectDeviceType();
    return deviceType === 'Android' || deviceType === 'iPhone' || deviceType === 'iPad';
}

export function persistDeviceType() {
    const deviceType = detectDeviceType();

    try {
        // Always overwrite — never leave a stale "Web" from a previous session
        // or from Chrome desktop-site mis-detection.
        localStorage.setItem(STORAGE_KEYS.deviceType, deviceType);
    } catch {
        // Ignore storage failures.
    }

    logDetectionTrace('persistDeviceType (before save)', {
        loginSource:
            deviceType === 'Web' ? 'Web' : 'Mobile',
        deviceType,
    });

    return deviceType;
}

/**
 * True when running inside a native mobile WebView / shell
 * (as opposed to a normal mobile browser).
 */
export function isMobileWebView() {
    if (typeof window === 'undefined') {
        return false;
    }

    const s = getNavigatorSignals();

    const nativeStored =
        isTruthyFlag(safeGetStorage(sessionStorage, STORAGE_KEYS.isMobileApp)) ||
        isTruthyFlag(safeGetStorage(localStorage, STORAGE_KEYS.isMobileApp)) ||
        isTruthyFlag(safeGetStorage(sessionStorage, STORAGE_KEYS.spliytNativeApp)) ||
        isTruthyFlag(safeGetStorage(localStorage, STORAGE_KEYS.spliytNativeApp));

    if (nativeStored) {
        persistDeviceType();
        return true;
    }

    if (hasNativeBridge()) {
        persistMobileSession();
        persistDeviceType();
        return true;
    }

    if (hasMobileUserAgent(s)) {
        persistMobileSession();
        persistDeviceType();
        return true;
    }

    if (isIOSWebView(s) || isAndroidWebView(s)) {
        persistMobileSession();
        persistDeviceType();
        return true;
    }

    return false;
}

/**
 * Resolve loginSource for the current session.
 * Any Android / iPhone / iPad (browser or WebView) → "Mobile"
 * Desktop / laptop → "Web"
 *
 * Always re-detects from live navigator signals so a stale localStorage
 * "Web" value cannot stick on a real phone (common after Chrome desktop-site).
 */
export function getLoginSource() {
    const deviceType = persistDeviceType();

    if (deviceType === 'Android' || deviceType === 'iPhone' || deviceType === 'iPad') {
        if (isMobileWebView()) {
            persistMobileSession();
        } else {
            persistMobileLoginSource();
        }
        logDetectionTrace('getLoginSource', { loginSource: 'Mobile', deviceType });
        return 'Mobile';
    }

    // Final safety net: never persist Web if Client Hints / spoof recovery still say mobile.
    const hintsType = resolveFromClientHints(getNavigatorSignals());
    if (hintsType) {
        try {
            localStorage.setItem(STORAGE_KEYS.deviceType, hintsType);
        } catch {
            // Ignore
        }
        persistMobileLoginSource();
        logDetectionTrace('getLoginSource (hints fallback)', {
            loginSource: 'Mobile',
            deviceType: hintsType,
        });
        return 'Mobile';
    }

    if (isAndroidDesktopUaSpoof(getNavigatorSignals())) {
        try {
            localStorage.setItem(STORAGE_KEYS.deviceType, 'Android');
        } catch {
            // Ignore
        }
        persistMobileLoginSource();
        logDetectionTrace('getLoginSource (Chrome desktop-spoof fallback)', {
            loginSource: 'Mobile',
            deviceType: 'Android',
        });
        return 'Mobile';
    }

    // Direct write (not persistWebSession) — we already verified desktop above.
    // persistWebSession re-checks detectDeviceType and could disagree if signals race.
    safeSetStorage(sessionStorage, STORAGE_KEYS.loginSource, 'Web');
    safeSetStorage(localStorage, STORAGE_KEYS.loginSource, 'Web');
    safeSetStorage(sessionStorage, STORAGE_KEYS.isMobileApp, 'false');
    safeSetStorage(localStorage, STORAGE_KEYS.isMobileApp, 'false');
    safeSetStorage(localStorage, STORAGE_KEYS.deviceType, 'Web');
    logDetectionTrace('getLoginSource', { loginSource: 'Web', deviceType: 'Web' });
    return 'Web';
}

/**
 * Async refinement using high-entropy Client Hints.
 * Needed when Chrome fully spoofs low-entropy hints to Linux desktop on tablets.
 * Updates storage in place; never downgrades Mobile → Web.
 */
function refineWithHighEntropyClientHints() {
    try {
        if (
            typeof navigator === 'undefined' ||
            !navigator.userAgentData ||
            typeof navigator.userAgentData.getHighEntropyValues !== 'function'
        ) {
            return;
        }

        navigator.userAgentData
            .getHighEntropyValues([
                'platform',
                'platformVersion',
                'model',
                'architecture',
                'bitness',
                'formFactors',
            ])
            .then((values) => {
                if (!values || typeof values !== 'object') {
                    return;
                }

                const platform = String(values.platform || '');
                const architecture = String(values.architecture || '');
                const model = String(values.model || '');
                const formFactors = Array.isArray(values.formFactors)
                    ? values.formFactors.map((f) => String(f).toLowerCase())
                    : [];

                const isMobileForm =
                    formFactors.some((f) => f === 'mobile' || f === 'tablet' || f === 'xr');
                const isAndroidHint =
                    /Android/i.test(platform) ||
                    (/arm/i.test(architecture) && isMobileForm) ||
                    (model !== '' && isMobileForm && !/Windows|macOS|Chrome OS/i.test(platform));

                if (!isAndroidHint && !isMobileForm) {
                    return;
                }

                // Never overwrite an already-correct mobile deviceType with Web;
                // only upgrade Web → Android (or keep existing mobile types).
                const current = safeGetStorage(localStorage, STORAGE_KEYS.deviceType);
                if (current === 'iPhone' || current === 'iPad' || current === 'Android') {
                    persistMobileLoginSource();
                    return;
                }

                const nextType = /iPhone|iPod/i.test(getNavigatorSignals().ua)
                    ? 'iPhone'
                    : isIPadPlatform()
                        ? 'iPad'
                        : 'Android';

                safeSetStorage(localStorage, STORAGE_KEYS.deviceType, nextType);
                persistMobileLoginSource();
                logDetectionTrace('refineWithHighEntropyClientHints', {
                    loginSource: 'Mobile',
                    deviceType: nextType,
                    highEntropy: {
                        platform,
                        architecture,
                        model,
                        formFactors,
                    },
                });
            })
            .catch(() => {
                // High-entropy hints may be denied; sync detection already ran.
            });
    } catch {
        // Ignore
    }
}

export function initLoginSourceDetection() {
    const fromUrl = persistLoginSourceFromUrl();
    let resolved;

    if (fromUrl === 'Mobile') {
        // URL asked for Mobile — still refresh deviceType from live signals.
        // Do not call persistMobileSession() here: that would mark every mobile
        // browser as a native app (isMobileApp). Only native shells should.
        persistMobileLoginSource();
        resolved = 'Mobile';
    } else if (fromUrl === 'Web' && !isMobileDevice()) {
        persistWebSession();
        resolved = 'Web';
    } else {
        // Live detection wins over a stale ?loginSource=web on mobile,
        // and over any previous localStorage "Web" value.
        resolved = getLoginSource();
    }

    const deviceType = persistDeviceType();

    // Absolute guard: mobile OS must never remain stored as Web.
    if (
        (deviceType === 'Android' || deviceType === 'iPhone' || deviceType === 'iPad') &&
        resolved !== 'Mobile'
    ) {
        persistMobileLoginSource();
        resolved = 'Mobile';
    }

    // Second absolute guard: if storage somehow still says Web, force Mobile.
    const storedSource = safeGetStorage(localStorage, STORAGE_KEYS.loginSource);
    if (
        (deviceType === 'Android' || deviceType === 'iPhone' || deviceType === 'iPad') &&
        storedSource != null &&
        String(storedSource).trim().toLowerCase() === 'web'
    ) {
        persistMobileLoginSource();
        resolved = 'Mobile';
    }

    logDetectionTrace('initLoginSourceDetection', {
        loginSource: resolved,
        deviceType,
        fromUrl,
        hasNativeBridge: hasNativeBridge(),
        isAndroidPlatform: isAndroidPlatform(),
        isAndroidDesktopUaSpoof: isAndroidDesktopUaSpoof(),
        isIPhonePlatform: isIPhonePlatform(),
        isIPadPlatform: isIPadPlatform(),
        storedLoginSource: safeGetStorage(localStorage, STORAGE_KEYS.loginSource),
        storedDeviceType: safeGetStorage(localStorage, STORAGE_KEYS.deviceType),
    });

    // Async upgrade path for fully spoofed Chrome tablet desktop mode.
    refineWithHighEntropyClientHints();

    return resolved;
}
